import { useState, useEffect } from 'react';
import { productsAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Package,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from 'lucide-react';
import { toast } from 'sonner';

const ITEMS_PER_PAGE = 10;

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockProduct, setStockProduct] = useState(null);
  const [stockQty, setStockQty] = useState('');
  const [stockOp, setStockOp] = useState('add');
  const [currentPage, setCurrentPage] = useState(1);
  const [showFinance, setShowFinance] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    sku: '',
    unit_price: '',
    cost_price_percent: '75',
    cost_price: '',
    discount_percent: '0',
    sell_price: '',
    customer_discount: '',
    profit: '',
    stock: '0',
    low_stock_threshold: '5',
  });

  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    try {
      const res = await productsAPI.getAll({ search: search || undefined });
      setProducts(res.data);
      setCurrentPage(1);
    } catch (error) {
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const toNumber = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
  };

  const calculateCostPrice = (unitPrice, costPercent) => {
    const unit = toNumber(unitPrice);
    const percent = toNumber(costPercent);
    return ((unit * percent) / 100).toFixed(2);
  };

  const calculateUnitPrice = (costPrice, costPercent) => {
    const cost = toNumber(costPrice);
    const percent = toNumber(costPercent);

    if (percent <= 0) return '0.00';

    return (cost / (percent / 100)).toFixed(2);
  };

  const calculateCostPercent = (unitPrice, costPrice) => {
    const unit = toNumber(unitPrice);
    const cost = toNumber(costPrice);

    if (unit <= 0) return '0.00';

    return ((cost / unit) * 100).toFixed(2);
  };

  const calculateSellPrice = (unitPrice, discountPercent) => {
    const unit = toNumber(unitPrice);
    const discount = toNumber(discountPercent);
    return (unit * (1 - discount / 100)).toFixed(2);
  };

  const calculateCustomerDiscount = (unitPrice, sellPrice) => {
    const unit = toNumber(unitPrice);
    const sell = toNumber(sellPrice);
    return Math.max(0, unit - sell).toFixed(2);
  };

  const calculateProfit = (sellPrice, costPrice) => {
    const sell = toNumber(sellPrice);
    const cost = toNumber(costPrice);
    return (sell - cost).toFixed(2);
  };

  const handleUnitPriceChange = (value) => {
    const costPrice = calculateCostPrice(value, formData.cost_price_percent);
    const sellPrice = calculateSellPrice(value, formData.discount_percent);
    const customerDiscount = calculateCustomerDiscount(value, sellPrice);
    const profit = calculateProfit(sellPrice, costPrice);

    setFormData((prev) => ({
      ...prev,
      unit_price: value,
      cost_price: costPrice,
      sell_price: sellPrice,
      customer_discount: customerDiscount,
      profit,
    }));
  };

  const handleCostPercentChange = (value) => {
    const costPrice = calculateCostPrice(formData.unit_price, value);
    const sellPrice = calculateSellPrice(formData.unit_price, formData.discount_percent);
    const customerDiscount = calculateCustomerDiscount(formData.unit_price, sellPrice);
    const profit = calculateProfit(sellPrice, costPrice);

    setFormData((prev) => ({
      ...prev,
      cost_price_percent: value,
      cost_price: costPrice,
      sell_price: sellPrice,
      customer_discount: customerDiscount,
      profit,
    }));
  };

  const handleCostPriceChange = (value) => {
    const unitPrice = calculateUnitPrice(value, formData.cost_price_percent);
    const sellPrice = calculateSellPrice(unitPrice, formData.discount_percent);
    const customerDiscount = calculateCustomerDiscount(unitPrice, sellPrice);
    const profit = calculateProfit(sellPrice, value);

    setFormData((prev) => ({
      ...prev,
      cost_price: value,
      unit_price: unitPrice,
      sell_price: sellPrice,
      customer_discount: customerDiscount,
      profit,
    }));
  };

  const handleDiscountPercentChange = (value) => {
    const sellPrice = calculateSellPrice(formData.unit_price, value);
    const customerDiscount = calculateCustomerDiscount(formData.unit_price, sellPrice);
    const profit = calculateProfit(sellPrice, formData.cost_price);

    setFormData((prev) => ({
      ...prev,
      discount_percent: value,
      sell_price: sellPrice,
      customer_discount: customerDiscount,
      profit,
    }));
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);

      const unitPrice = product.unit_price ?? product.sell_price ?? 0;
      const costPrice = product.cost_price ?? 0;
      const discountPercent = (product.discount_percent ?? 0).toString();
      const sellPrice = product.sell_price ?? unitPrice;
      const customerDiscount = calculateCustomerDiscount(unitPrice, sellPrice);
      const profit = calculateProfit(sellPrice, costPrice);
      const costPercent =
        product.cost_price_percent?.toString() ??
        calculateCostPercent(unitPrice, costPrice);

      setFormData({
        name: product.name,
        sku: product.sku || '',
        unit_price: unitPrice.toString(),
        cost_price_percent: costPercent,
        cost_price: costPrice.toString(),
        discount_percent: discountPercent,
        sell_price: sellPrice.toString(),
        customer_discount: customerDiscount,
        profit,
        stock: String(product.stock ?? 0),
        low_stock_threshold: String(product.low_stock_threshold ?? 5),
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        sku: '',
        unit_price: '',
        cost_price_percent: '75',
        cost_price: '',
        discount_percent: '0',
        sell_price: '',
        customer_discount: '0',
        profit: '',
        stock: '0',
        low_stock_threshold: '5',
      });
    }

    setShowModal(true);
  };

  const handleOpenStockModal = (product) => {
    setStockProduct(product);
    setStockQty('');
    setStockOp('add');
    setShowStockModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);

    const payload = {
      name: formData.name,
      sku: formData.sku || null,
      unit_price: toNumber(formData.unit_price),
      cost_price: toNumber(formData.cost_price),
      discount_percent: toNumber(formData.discount_percent),
      sell_price: toNumber(formData.sell_price),
      stock: parseInt(formData.stock, 10) || 0,
      low_stock_threshold: parseInt(formData.low_stock_threshold, 10) || 0,
    };

    try {
      if (editingProduct) {
        await productsAPI.update(editingProduct.id, payload);
        toast.success('Product updated');
      } else {
        await productsAPI.create(payload);
        toast.success('Product created');
      }

      setShowModal(false);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateStock = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await productsAPI.updateStock(stockProduct.id, parseInt(stockQty, 10), stockOp);
      toast.success('Stock updated');
      setShowStockModal(false);
      fetchProducts();
    } catch (error) {
      toast.error('Failed to update stock');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (product) => {
    if (!window.confirm(`Delete product "${product.name}"?`)) return;

    try {
      await productsAPI.delete(product.id);
      toast.success('Product deleted');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete product');
    }
  };

  const isLowStock = (product) => product.stock <= product.low_stock_threshold;

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);

  const paginatedProducts = products.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  return (
    <div className="space-y-6 animate-fade-in" data-testid="products-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black font-heading tracking-tight">
            Products & Parts
          </h1>
          <p className="text-muted-foreground mt-1">Manage your inventory</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={() => setShowFinance((prev) => !prev)}
            data-testid="toggle-finance-columns-btn"
          >
            {showFinance ? (
              <>
                <EyeOff className="w-4 h-4 mr-2" />
                Hide Cost/Profit
              </>
            ) : (
              <>
                <Eye className="w-4 h-4 mr-2" />
                Show Cost/Profit
              </>
            )}
          </Button>

          <Button
            onClick={() => handleOpenModal()}
            className="font-semibold uppercase tracking-wide"
            data-testid="add-product-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Product
          </Button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          placeholder="Search by name or SKU..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="product-search-input"
        />
      </div>

      <Card data-testid="products-table-card">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="spinner" />
            </div>
          ) : products.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product</TableHead>
                      <TableHead>SKU</TableHead>
                      <TableHead className="text-right">Unit Price</TableHead>
                      <TableHead className="text-right">Sell Price</TableHead>
                      {showFinance && (
                        <TableHead className="text-right">Cost Price</TableHead>
                      )}
                      {showFinance && (
                        <TableHead className="text-right">Profit</TableHead>
                      )}
                      <TableHead className="text-center">Stock</TableHead>
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>

                  <TableBody>
                    {paginatedProducts.map((product) => {
                      const unitPrice = product.unit_price ?? product.sell_price ?? 0;
                      const sellPrice = product.sell_price ?? 0;
                      const costPrice = product.cost_price ?? 0;
                      const profit = sellPrice - costPrice;

                      return (
                        <TableRow key={product.id} data-testid={`product-row-${product.id}`}>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <div
                                className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                                  isLowStock(product)
                                    ? 'bg-red-100 dark:bg-red-900/30'
                                    : 'bg-slate-100 dark:bg-slate-800'
                                }`}
                              >
                                <Package
                                  className={`w-4 h-4 ${
                                    isLowStock(product)
                                      ? 'text-red-600 dark:text-red-400'
                                      : 'text-slate-600 dark:text-slate-400'
                                  }`}
                                />
                              </div>
                              <span className="font-medium">{product.name}</span>
                            </div>
                          </TableCell>

                          <TableCell className="font-mono text-muted-foreground">
                            {product.sku || '-'}
                          </TableCell>

                          <TableCell className="text-right font-mono">
                            {formatCurrency(unitPrice)}
                          </TableCell>

                          <TableCell className="text-right font-mono">
                            {formatCurrency(sellPrice)}
                          </TableCell>

                          {showFinance && (
                            <TableCell className="text-right font-mono text-muted-foreground">
                              {formatCurrency(costPrice)}
                            </TableCell>
                          )}

                          {showFinance && (
                            <TableCell className="text-right font-mono text-green-600 dark:text-green-400">
                              {formatCurrency(profit)}
                            </TableCell>
                          )}

                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleOpenStockModal(product)}
                              className={isLowStock(product) ? 'text-red-600 hover:text-red-700' : ''}
                              data-testid={`stock-btn-${product.id}`}
                            >
                              {isLowStock(product) && <AlertTriangle className="w-4 h-4 mr-1" />}
                              {product.stock}
                            </Button>
                          </TableCell>

                          <TableCell>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleOpenModal(product)}
                                data-testid={`edit-product-${product.id}`}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>

                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(product)}
                                className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                data-testid={`delete-product-${product.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t">
                  <p className="text-sm text-muted-foreground">
                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to{' '}
                    {Math.min(currentPage * ITEMS_PER_PAGE, products.length)} of {products.length}
                  </p>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>

                    <span className="text-sm px-2">
                      Page {currentPage} of {totalPages}
                    </span>

                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state py-12">
              <Package className="w-16 h-16 mx-auto text-muted-foreground/50" />
              <p className="mt-4 text-lg">No products found</p>
              <Button onClick={() => handleOpenModal()} className="mt-4">
                Add Your First Product
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg" data-testid="product-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingProduct ? 'Edit Product' : 'Add Product'}
            </DialogTitle>
            <DialogDescription>
              {editingProduct ? 'Update product details' : 'Add a new product to your inventory'}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSave} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Product Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Oil Filter"
                  required
                  data-testid="product-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sku">SKU</Label>
                <Input
                  id="sku"
                  value={formData.sku}
                  onChange={(e) =>
                    setFormData({ ...formData, sku: e.target.value.toUpperCase() })
                  }
                  placeholder="OIL-001"
                  data-testid="product-sku-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="unit_price">Unit Price *</Label>
              <Input
                id="unit_price"
                type="number"
                step="0.01"
                min="0"
                value={formData.unit_price}
                onChange={(e) => handleUnitPriceChange(e.target.value)}
                placeholder="Enter unit price"
                required
                data-testid="product-unit-price-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cost_price_percent">Cost Price %</Label>
                <Input
                  id="cost_price_percent"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.cost_price_percent}
                  onChange={(e) => handleCostPercentChange(e.target.value)}
                  placeholder="75"
                  data-testid="product-cost-percent-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cost_price">Cost Price</Label>
                <Input
                  id="cost_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.cost_price}
                  onChange={(e) => handleCostPriceChange(e.target.value)}
                  placeholder="Enter cost price"
                  data-testid="product-cost-price-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="discount_percent">Discount %</Label>
                <Input
                  id="discount_percent"
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={formData.discount_percent}
                  onChange={(e) => handleDiscountPercentChange(e.target.value)}
                  placeholder="0"
                  data-testid="product-discount-percent-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="sell_price">Sell Price</Label>
                <Input
                  id="sell_price"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.sell_price}
                  readOnly
                  className="bg-muted"
                  data-testid="product-sell-price-input"
                />
              </div>
            </div>

            {formData.unit_price && (
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Customer Discount:</span>
                  <span className="font-mono font-bold text-orange-600 dark:text-orange-400">
                    {formatCurrency(toNumber(formData.customer_discount))}
                  </span>
                </div>

                <div className="flex justify-between text-sm border-t pt-2">
                  <span className="text-muted-foreground">Profit (Sell - Cost):</span>
                  <span
                    className={`font-mono font-bold ${
                      toNumber(formData.profit) >= 0
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                    }`}
                  >
                    {formatCurrency(toNumber(formData.profit))}
                  </span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="stock">Initial Stock</Label>
                <Input
                  id="stock"
                  type="number"
                  min="0"
                  value={formData.stock}
                  onChange={(e) => setFormData({ ...formData, stock: e.target.value })}
                  placeholder="10"
                  data-testid="product-stock-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="low_stock_threshold">Low Stock Alert</Label>
                <Input
                  id="low_stock_threshold"
                  type="number"
                  min="0"
                  value={formData.low_stock_threshold}
                  onChange={(e) =>
                    setFormData({ ...formData, low_stock_threshold: e.target.value })
                  }
                  placeholder="5"
                  data-testid="product-threshold-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="save-product-btn">
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={showStockModal} onOpenChange={setShowStockModal}>
        <DialogContent data-testid="stock-modal">
          <DialogHeader>
            <DialogTitle className="font-heading">Update Stock</DialogTitle>
            <DialogDescription>
              {stockProduct?.name} - Current stock: {stockProduct?.stock}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleUpdateStock} className="space-y-4">
            <div className="flex gap-4">
              <Button
                type="button"
                variant={stockOp === 'add' ? 'default' : 'outline'}
                onClick={() => setStockOp('add')}
                className="flex-1"
              >
                Add Stock
              </Button>

              <Button
                type="button"
                variant={stockOp === 'subtract' ? 'default' : 'outline'}
                onClick={() => setStockOp('subtract')}
                className="flex-1"
              >
                Remove Stock
              </Button>
            </div>

            <div className="space-y-2">
              <Label htmlFor="qty">Quantity</Label>
              <Input
                id="qty"
                type="number"
                min="1"
                value={stockQty}
                onChange={(e) => setStockQty(e.target.value)}
                placeholder="Enter quantity"
                required
                data-testid="stock-qty-input"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowStockModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} data-testid="update-stock-btn">
                {saving ? 'Updating...' : 'Update'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}